import React, { useEffect, useState, useCallback } from 'react';
import { 
  AnalyticalTable, 
  Button, 
  Dialog, 
  Title, 
  Select, 
  Option, 
  BusyIndicator, 
  Input, 
  Bar,
  Form,
  FormItem,
  FormGroup,
  MessageBox,
  MessageBoxType,
  MessageBoxAction,
  Text,
  Icon,
  Label,
  Switch
} from '@ui5/webcomponents-react';
import axiosInstance from '../utils/axiosInstance';
import usePermission from '../hooks/usePermission';
import withPermission from './withPermission';
import { parseToNumber, formatNumberWithComma } from '../utils/validator';
import useAutoTranslate from '../hooks/useAutoTranslate';

const TruckTypeList = () => {
  const { hasPermission } = usePermission();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    sortBy: 'name',
    sortDirection: 'asc'
  });
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedTruckType, setSelectedTruckType] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    maxWeightTons: 0,
    maxVolumeCubicMeters: 0,
    lengthMeters: 0,
    widthMeters: 0,
    heightMeters: 0,
    axlesCount: 2,
    hasRefrigeration: false,
    hasHydraulicLift: false,
    description: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const { autoT } = useAutoTranslate();

  const fetchTruckTypes = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const url = `truck-types?page=${page}&size=${pageSize}`;
      const response = await axiosInstance.get(url);
      if (response.data.records.code === "SUCCESS") {
        setData(response.data.records.data);
        setTotalPages(Math.ceil(response.data.totalCount / pageSize));
      } else {
        setError(response.data.records.message);
      }
    } catch (err) {
      setError('Error al cargar los tipos de equipos: ' + (err.message || 'Error desconocido'));
    } finally {
      setLoading(false);
    }
  }, [pageSize]);

  useEffect(() => {
    fetchTruckTypes(currentPage);
  }, [currentPage, fetchTruckTypes]);

  useEffect(() => {
    let filtered = [...data];
    
    // Filtrar por término de búsqueda
    if (searchTerm) {
      const lowerCaseSearchTerm = searchTerm.toLowerCase();
      filtered = filtered.filter(truckType => 
        truckType.code.toLowerCase().includes(lowerCaseSearchTerm) ||
        truckType.name.toLowerCase().includes(lowerCaseSearchTerm) ||
        truckType.description.toLowerCase().includes(lowerCaseSearchTerm)
      );
    }
    
    // Ordenar los datos
    filtered.sort((a, b) => {
      const valueA = a[sortConfig.sortBy];
      const valueB = b[sortConfig.sortBy];
      
      if (typeof valueA === 'string' && typeof valueB === 'string') {
        return sortConfig.sortDirection === 'asc' 
          ? valueA.localeCompare(valueB) 
          : valueB.localeCompare(valueA);
      } else {
        return sortConfig.sortDirection === 'asc' 
          ? valueA - valueB 
          : valueB - valueA;
      }
    });
    
    setFilteredData(filtered);
  }, [data, searchTerm, sortConfig]);

  const handleSort = (column) => {
    setSortConfig(prevConfig => ({
      sortBy: column,
      sortDirection: prevConfig.sortBy === column && prevConfig.sortDirection === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  // Función para manejar el clic en una fila
  const handleRowClick = (event) => {
    // Solo permitir editar si tiene el permiso de actualización
    if (!hasPermission('truckstypes', 'update')) {
      return;
    }
    
    const { detail } = event;
    if (detail && detail.row && detail.row.original) {
      const truckType = detail.row.original;
      setSelectedTruckType(truckType);
      
      // Inicializar el formulario con los datos del tipo de equipo seleccionado
      setFormData({
        code: truckType.code || '',
        name: truckType.name || '',
        maxWeightTons: truckType.maxWeightTons || 0,
        maxVolumeCubicMeters: truckType.maxVolumeCubicMeters || 0,
        lengthMeters: truckType.lengthMeters || 0,
        widthMeters: truckType.widthMeters || 0,
        heightMeters: truckType.heightMeters || 0,
        axlesCount: truckType.axlesCount || 2,
        hasRefrigeration: truckType.hasRefrigeration || false,
        hasHydraulicLift: truckType.hasHydraulicLift || false,
        description: truckType.description || ''
      });
      
      // Mostrar el diálogo de edición
      setShowEditDialog(true);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const handleCreateClick = () => {
    // Solo permitir crear tipos de equipos si tiene el permiso de creación
    if (!hasPermission('truckstypes', 'create')) {
      return;
    }
    
    setFormData({
      code: '',
      name: '',
      maxWeightTons: 0,
      maxVolumeCubicMeters: 0,
      lengthMeters: 0,
      widthMeters: 0,
      heightMeters: 0,
      axlesCount: 2,
      hasRefrigeration: false,
      hasHydraulicLift: false,
      description: ''
    });
    setFormErrors({});
    setShowCreateDialog(true);
  };

  const handleEditClick = (truckType) => {
    // Solo permitir editar tipos de equipos si tiene el permiso de actualización
    if (!hasPermission('truckstypes', 'update')) {
      return;
    }
    
    setSelectedTruckType(truckType);
    setFormData({
      code: truckType.code || '',
      name: truckType.name || '',
      maxWeightTons: truckType.maxWeightTons || 0,
      maxVolumeCubicMeters: truckType.maxVolumeCubicMeters || 0,
      lengthMeters: truckType.lengthMeters || 0,
      widthMeters: truckType.widthMeters || 0,
      heightMeters: truckType.heightMeters || 0,
      axlesCount: truckType.axlesCount || 2,
      hasRefrigeration: truckType.hasRefrigeration || false,
      hasHydraulicLift: truckType.hasHydraulicLift || false,
      description: truckType.description || ''
    });
    setFormErrors({});
    setShowEditDialog(true);
  };

  const handleDeleteClick = (truckType) => {
    // Solo permitir eliminar tipos de equipos si tiene el permiso de eliminación
    if (!hasPermission('truckstypes', 'delete')) {
      return;
    }
    
    setSelectedTruckType(truckType);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedTruckType) return;
    
    try {
      await axiosInstance.delete(`truck-types/${selectedTruckType.id}`);
      fetchTruckTypes(currentPage);
      setShowDeleteConfirm(false);
    } catch (err) {
      setError('Error al eliminar el tipo de equipo: ' + (err.message || 'Error desconocido'));
    }
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
      setFormData({
        ...formData,
        [name]: value
      });
    if (formErrors[name]) {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleNumberInputChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: parseToNumber(value)
    }));
    
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSwitchChange = (event) => {
    const { name, checked } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.code) {
      errors.code = 'El código es obligatorio';
    }
    
    if (!formData.name) {
      errors.name = 'El nombre es obligatorio';
    }
    
    if (formData.maxWeightTons <= 0) {
      errors.maxWeightTons = 'El peso máximo debe ser mayor a 0';
    }
    
    if (formData.maxVolumeCubicMeters <= 0) {
      errors.maxVolumeCubicMeters = 'El volumen máximo debe ser mayor a 0';
    }
    
    if (formData.lengthMeters <= 0) {
      errors.lengthMeters = 'La longitud debe ser mayor a 0';
    }
    
    if (formData.widthMeters <= 0) {
      errors.widthMeters = 'El ancho debe ser mayor a 0';
    }
    
    if (formData.heightMeters <= 0) {
      errors.heightMeters = 'La altura debe ser mayor a 0';
    }
    
    if (formData.axlesCount <= 0) {
      errors.axlesCount = 'El número de ejes debe ser mayor a 0';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateSubmit = async () => {
    if (!validateForm()) return;
    
    try {
      const response = await axiosInstance.post('truck-types', formData);
      if (response.status === 201 || response.status === 200) {
        fetchTruckTypes(currentPage);
        setShowCreateDialog(false);
      } else {
        setError('Error al crear el tipo de equipo: ' + (response.data.message || 'Error desconocido'));
      }
    } catch (err) {
      setError('Error al crear el tipo de equipo: ' + (err.message || 'Error desconocido'));
    }
  };

  const handleEditSubmit = async () => {
    if (!validateForm() || !selectedTruckType) return;
    
    try {
      const response = await axiosInstance.put(`truck-types/${selectedTruckType.id}`, formData);
      if (response.status === 200) {
        fetchTruckTypes(currentPage);
        setShowEditDialog(false);
      } else {
        setError('Error al actualizar el tipo de equipo: ' + (response.data.message || 'Error desconocido'));
      }
    } catch (err) {
      setError('Error al actualizar el tipo de equipo: ' + (err.message || 'Error desconocido'));
    }
  };

  const rightAlignedNumberStyle = () => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    width: '100%'
  });

  const columns = [
    {
      Header: 'Código',
      accessor: 'code',
      headerProps: {
        onClick: () => handleSort('code')
      },
      className: sortConfig.sortBy === 'code' ? `sorted-${sortConfig.sortDirection}` : ''
    },
    {
      Header: 'Nombre',
      accessor: 'name',
      headerProps: {
        onClick: () => handleSort('name')
      },
      className: sortConfig.sortBy === 'name' ? `sorted-${sortConfig.sortDirection}` : ''
    },
    {
      Header: 'Peso Máx. (Kg)',
      accessor: 'maxWeightTons',
      Cell: ({ value }) => (
        <Text style={{ textAlign: 'right', display: 'block', width: '100%' }}>
          {value}
        </Text>
      ),
      headerProps: {
        onClick: () => handleSort('maxWeightTons')
      },
      className: sortConfig.sortBy === 'maxWeightTons' ? `sorted-${sortConfig.sortDirection}` : '',
      Cell: ({ value }) => (
        <div style={rightAlignedNumberStyle()}>
          <span>
            {formatNumberWithComma(parseToNumber(value))} kg
          </span>
        </div>
      )
    },
    {
      Header: 'Volumen Máx. (m³)',
      accessor: 'maxVolumeCubicMeters',
      Cell: ({ value }) => (
        <Text style={{ textAlign: 'right', display: 'block', width: '100%' }}>
          {value}
        </Text>
      ),
      headerProps: {
        onClick: () => handleSort('maxVolumeCubicMeters')
      },
      className: sortConfig.sortBy === 'maxVolumeCubicMeters' ? `sorted-${sortConfig.sortDirection}` : '',
      Cell: ({ value }) => (
        <div style={rightAlignedNumberStyle()}>
          <span>
            {formatNumberWithComma(parseToNumber(value))} m³
          </span>
        </div>
      )
    },
    {
      Header: 'Dimensiones (m)',
      accessor: 'dimensions',
      Cell: ({ row }) => {
        const { lengthMeters, widthMeters, heightMeters } = row.original;
        return (
          <Text style={{ textAlign: 'right', display: 'block', width: '100%' }}>
            {`${lengthMeters} x ${widthMeters} x ${heightMeters}`}
          </Text>
        );
      },
      hAlign: "End"
    },
    {
      Header: 'Ejes',
      accessor: 'axlesCount',
      Cell: ({ value }) => (
        <Text style={{ textAlign: 'right', display: 'block', width: '100%' }}>
          {value}
        </Text>
      ),
      headerProps: {
        onClick: () => handleSort('axlesCount')
      },
      className: sortConfig.sortBy === 'axlesCount' ? `sorted-${sortConfig.sortDirection}` : '',
      hAlign: "End"
    },
    {
      Header: 'Refrigerado',
      accessor: 'hasRefrigeration',
      Cell: ({ value }) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Icon 
            name={value ? "status-positive" : "status-negative"} 
            style={{ marginRight: '8px', color: value ? '#28a745' : '#dc3545' }} 
          />
          <Text>{value ? 'Sí' : 'No'}</Text>
        </div>
      ),
      headerProps: {
        onClick: () => handleSort('hasRefrigeration')
      },
      className: sortConfig.sortBy === 'hasRefrigeration' ? `sorted-${sortConfig.sortDirection}` : ''
    },
    {
      Header: 'Elevador Hidráulico',
      accessor: 'hasHydraulicLift',
      Cell: ({ value }) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Icon 
            name={value ? "status-positive" : "status-negative"} 
            style={{ marginRight: '8px', color: value ? '#28a745' : '#dc3545' }} 
          />
          <Text>{value ? 'Sí' : 'No'}</Text>
        </div>
      ),
      headerProps: {
        onClick: () => handleSort('hasHydraulicLift')
      },
      className: sortConfig.sortBy === 'hasHydraulicLift' ? `sorted-${sortConfig.sortDirection}` : ''
    },
    {
      Header: 'Acciones',
      id: 'actions',
      Cell: ({ row }) => (
        <div style={{ display: 'flex', gap: '8px' }}>
          {hasPermission('truckstypes', 'update') && (
            <Button 
              icon="edit" 
              design="Transparent" 
              onClick={(e) => {
                e.stopPropagation();
                handleEditClick(row.original);
              }}
            />
          )}
          {hasPermission('truckstypes', 'delete') && (
            <Button 
              icon="delete" 
              design="Transparent" 
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteClick(row.original);
              }}
            />
          )}
        </div>
      )
    }
  ];

  return (
    <div style={{
      padding: '1.5rem',
      backgroundColor: 'var(--sapBackgroundColor)',
      minHeight: '100vh'
    }}>
      <div style={{ padding: '1rem' }}>
        <Title level="H2">{autoT('Gestión de tipos de equipos')}</Title>
        
        {error && (
          <div style={{ color: 'red', margin: '1rem 0' }}>
            {error}
          </div>
        )}
        
        <Bar
          startContent={
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
              <Input
                placeholder={autoT('Buscar tipo de equipo...')}
                value={searchTerm}
                onChange={handleSearchChange}
                style={{ width: '250px' }}
              />
            </div>
          }
          endContent={
            hasPermission('truckstypes', 'create') && (
              <Button 
                icon="add" 
                design="Emphasized"
                onClick={handleCreateClick}
              >
                {autoT('Nuevo Tipo de equipo')}
              </Button>
            )
          }
        />
        
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
            <BusyIndicator active size="Large" />
          </div>
        ) : (
          <>
            <AnalyticalTable
              data={filteredData}
              columns={columns}
              selectionMode="None"
              onRowClick={hasPermission('truckstypes', 'update') ? handleRowClick : undefined}
              style={{ marginTop: '1rem' }}
              visibleRows={10}
              minRows={10}
              alternateRowColor
            />
            
            <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', marginTop: '1rem' }}>
              <Button 
                icon="navigation-left-arrow" 
                disabled={currentPage === 1}
                onClick={handlePrevPage}
              >
                Anterior
              </Button>
              <Text style={{ alignSelf: 'center' }}>
                Página {currentPage} de {totalPages}
              </Text>
              <Button 
                icon="navigation-right-arrow" 
                disabled={currentPage === totalPages}
                onClick={handleNextPage}
                iconEnd
              >
                Siguiente
              </Button>
            </div>
          </>
        )}
      </div>
      
      {/* Dialog para crear tipo de equipo */}
      <Dialog
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        header={
          <Bar
            startContent={<Title level="H4">{showCreateDialog ? autoT('Crear Nuevo Tipo de equipo') : `${autoT('Editar Tipo de equipo')}: ${selectedTruckType?.name}`}</Title>}
          />
        }
        footer={
          <Bar
            endContent={
              <>
                <Button design="Emphasized" onClick={handleCreateSubmit}>{autoT('Guardar')}</Button>
                <Button design="Transparent" onClick={() => setShowCreateDialog(false)}>{autoT('Cancelar')}</Button>
              </>
            }
          />
        }
        style={{ width: '800px' }}
      >
        <Form 
          style={{ padding: '1rem' }}
          titleText={autoT('Información del Tipo de equipo')}
          backgroundDesign="Transparent"
          columnsXL={2}
          columnsL={2}
          columnsM={2}
          columnsS={1}
          labelSpanXL={4}
          labelSpanL={4}
          labelSpanM={12}
          labelSpanS={12}
        >
          {formErrors.general && (
            <FormItem>
              <Text style={{ color: 'var(--sapErrorColor)', marginBottom: '1rem' }}>{formErrors.general}</Text>
            </FormItem>
          )}
          
          <FormGroup titleText={autoT('Información Básica')}>
            <FormItem labelContent={<Label>{autoT('Código')}</Label>}>
              <Input 
                name="code" 
                value={formData.code} 
                onChange={handleInputChange}
                valueState={formErrors.code ? "Error" : "None"}
                valueStateMessage={formErrors.code}
                required
              />
            </FormItem>
            <FormItem labelContent={<Label>{autoT('Nombre')}</Label>}>
              <Input 
                name="name" 
                value={formData.name} 
                onChange={handleInputChange}
                valueState={formErrors.name ? "Error" : "None"}
                valueStateMessage={formErrors.name}
                required
              />
            </FormItem>
            <FormItem labelContent={<Label>{autoT('Descripción')}</Label>}>
              <Input 
                name="description" 
                value={formData.description} 
                onChange={handleInputChange}
              />
            </FormItem>
          </FormGroup>
          
          <FormGroup titleText={autoT('Capacidad')}>
            <FormItem labelContent={<Label>{autoT('Peso Máximo (kg)')}</Label>}>
              <Input 
                name="maxWeightTons" 
                type="Text"
                value={formatNumberWithComma(formData.maxWeightTons)}
                onChange={handleInputChange}
                valueState={formErrors.maxWeightTons ? "Error" : "None"}
                valueStateMessage={formErrors.maxWeightTons}
                required
              />
            </FormItem>
            <FormItem labelContent={<Label>{autoT('Volumen Máximo (m³)')}</Label>}>
              <Input 
                name="maxVolumeCubicMeters" 
                type="Text"
                value={formatNumberWithComma(formData.maxVolumeCubicMeters)}
                onChange={handleInputChange}
                valueState={formErrors.maxVolumeCubicMeters ? "Error" : "None"}
                valueStateMessage={formErrors.maxVolumeCubicMeters}
                required
              />
            </FormItem>
          </FormGroup>
          
          <FormGroup titleText={autoT('Dimensiones')}>
            <FormItem labelContent={<Label>{autoT('Longitud (m)')}</Label>}>
              <Input 
                name="lengthMeters" 
                type="Number"
                value={formData.lengthMeters} 
                onChange={handleNumberInputChange}
                valueState={formErrors.lengthMeters ? "Error" : "None"}
                valueStateMessage={formErrors.lengthMeters}
                required
              />
            </FormItem>
            <FormItem labelContent={<Label>{autoT('Ancho (m)')}</Label>}>
              <Input 
                name="widthMeters" 
                type="Number"
                value={formData.widthMeters} 
                onChange={handleNumberInputChange}
                valueState={formErrors.widthMeters ? "Error" : "None"}
                valueStateMessage={formErrors.widthMeters}
                required
              />
            </FormItem>
            <FormItem labelContent={<Label>{autoT('Altura (m)')}</Label>}>
              <Input 
                name="heightMeters" 
                type="Number"
                value={formData.heightMeters} 
                onChange={handleNumberInputChange}
                valueState={formErrors.heightMeters ? "Error" : "None"}
                valueStateMessage={formErrors.heightMeters}
                required
              />
            </FormItem>
          </FormGroup>
          
          <FormGroup titleText={autoT('Características Adicionales')}>
            <FormItem labelContent={<Label>{autoT('Número de ejes')}</Label>}>
              <Input 
                name="axlesCount" 
                type="Number"
                value={formData.axlesCount} 
                onChange={handleNumberInputChange}
                valueState={formErrors.axlesCount ? "Error" : "None"}
                valueStateMessage={formErrors.axlesCount}
                required
              />
            </FormItem>
            <FormItem labelContent={<Label>{autoT('Refrigeración')}</Label>}>
              <Switch 
                name="hasRefrigeration"
                checked={formData.hasRefrigeration}
                onChange={handleSwitchChange}
                text={formData.hasRefrigeration ? "Sí" : "No"}
              />
            </FormItem>
            <FormItem labelContent={<Label>{autoT('Elevador Hidráulico')}</Label>}>
              <Switch 
                name="hasHydraulicLift"
                checked={formData.hasHydraulicLift}
                onChange={handleSwitchChange}
                text={formData.hasHydraulicLift ? "Sí" : "No"}
              />
            </FormItem>
          </FormGroup>
        </Form>
      </Dialog>

      {/* Dialog para editar tipo de equipo */}
      <Dialog
        open={showEditDialog}
        onClose={() => setShowEditDialog(false)}
        header={
          <Bar
            startContent={<Title level="H4">{showCreateDialog ? autoT('Crear Nuevo Tipo de equipo') : `${autoT('Editar Tipo de equipo')}: ${selectedTruckType?.name}`}</Title>}
          />
        }
        footer={
          <Bar
            startContent={
              hasPermission('truckstypes', 'delete') && (
                <Button design="Negative" onClick={handleDeleteClick} icon="delete">Eliminar</Button>
              )
            }
            endContent={
              <>
                <Button design="Emphasized" onClick={handleEditSubmit}>Guardar</Button>
                <Button design="Transparent" onClick={() => setShowEditDialog(false)}>Cancelar</Button>
              </>
            }
          />
        }
        style={{ width: '800px' }}
      >
        <Form 
          style={{ padding: '1rem' }}
          titleText={autoT('Información del Tipo de equipo')}
          backgroundDesign="Transparent"
          columnsXL={2}
          columnsL={2}
          columnsM={2}
          columnsS={1}
          labelSpanXL={4}
          labelSpanL={4}
          labelSpanM={12}
          labelSpanS={12}
        >
          {formErrors.general && (
            <FormItem>
              <Text style={{ color: 'var(--sapErrorColor)', marginBottom: '1rem' }}>{formErrors.general}</Text>
            </FormItem>
          )}
          
          <FormGroup titleText={autoT('Información Básica')}>
            <FormItem labelContent={<Label>{autoT('Código')}</Label>}>
              <Input 
                name="code" 
                value={formData.code} 
                onChange={handleInputChange}
                valueState={formErrors.code ? "Error" : "None"}
                valueStateMessage={formErrors.code}
                required
              />
            </FormItem>
            <FormItem labelContent={<Label>{autoT('Nombre')}</Label>}>
              <Input 
                name="name" 
                value={formData.name} 
                onChange={handleInputChange}
                valueState={formErrors.name ? "Error" : "None"}
                valueStateMessage={formErrors.name}
                required
              />
            </FormItem>
            <FormItem labelContent={<Label>{autoT('Descripción')}</Label>}>
              <Input 
                name="description" 
                value={formData.description} 
                onChange={handleInputChange}
              />
            </FormItem>
          </FormGroup>
          
          <FormGroup titleText={autoT('Capacidad')}>
            <FormItem labelContent={<Label>{autoT('Peso Máximo (kg)')}</Label>}>
              <Input 
                name="maxWeightTons" 
                type="Text"
                value={formatNumberWithComma(formData.maxWeightTons)}
                onChange={handleInputChange}
                valueState={formErrors.maxWeightTons ? "Error" : "None"}
                valueStateMessage={formErrors.maxWeightTons}
                required
              />
            </FormItem>
            <FormItem labelContent={<Label>{autoT('Volumen Máximo (m³)')}</Label>}>
              <Input 
                name="maxVolumeCubicMeters" 
                type="Text"
                value={formatNumberWithComma(formData.maxVolumeCubicMeters)}
                onChange={handleInputChange}
                valueState={formErrors.maxVolumeCubicMeters ? "Error" : "None"}
                valueStateMessage={formErrors.maxVolumeCubicMeters}
                required
              />
            </FormItem>
          </FormGroup>
          
          <FormGroup titleText={autoT('Dimensiones')}>
            <FormItem labelContent={<Label>{autoT('Longitud (m)')}</Label>}>
              <Input 
                name="lengthMeters" 
                type="Number"
                value={formData.lengthMeters} 
                onChange={handleNumberInputChange}
                valueState={formErrors.lengthMeters ? "Error" : "None"}
                valueStateMessage={formErrors.lengthMeters}
                required
              />
            </FormItem>
            <FormItem labelContent={<Label>{autoT('Ancho (m)')}</Label>}>
              <Input 
                name="widthMeters" 
                type="Number"
                value={formData.widthMeters} 
                onChange={handleNumberInputChange}
                valueState={formErrors.widthMeters ? "Error" : "None"}
                valueStateMessage={formErrors.widthMeters}
                required
              />
            </FormItem>
            <FormItem labelContent={<Label>{autoT('Altura (m)')}</Label>}>
              <Input 
                name="heightMeters" 
                type="Number"
                value={formData.heightMeters} 
                onChange={handleNumberInputChange}
                valueState={formErrors.heightMeters ? "Error" : "None"}
                valueStateMessage={formErrors.heightMeters}
                required
              />
            </FormItem>
          </FormGroup>
          
          <FormGroup titleText={autoT('Características Adicionales')}>
            <FormItem labelContent={<Label>{autoT('Número de ejes')}</Label>}>
              <Input 
                name="axlesCount" 
                type="Number"
                value={formData.axlesCount} 
                onChange={handleNumberInputChange}
                valueState={formErrors.axlesCount ? "Error" : "None"}
                valueStateMessage={formErrors.axlesCount}
                required
              />
            </FormItem>
            <FormItem labelContent={<Label>{autoT('Refrigeración')}</Label>}>
              <Switch 
                name="hasRefrigeration"
                checked={formData.hasRefrigeration}
                onChange={handleSwitchChange}
                text={formData.hasRefrigeration ? "Sí" : "No"}
              />
            </FormItem>
            <FormItem labelContent={<Label>{autoT('Elevador Hidráulico')}</Label>}>
              <Switch 
                name="hasHydraulicLift"
                checked={formData.hasHydraulicLift}
                onChange={handleSwitchChange}
                text={formData.hasHydraulicLift ? "Sí" : "No"}
              />
            </FormItem>
          </FormGroup>
        </Form>
      </Dialog>

      {/* Confirmación de eliminación */}
      <MessageBox
        open={showDeleteConfirm}
        onClose={(event) => {
          if (event.detail.action === MessageBoxAction.OK) {
            handleConfirmDelete();
          } else {
            setShowDeleteConfirm(false);
          }
        }}
        title="Confirmar eliminación"
        type={MessageBoxType.WARNING}
        actions={[MessageBoxAction.OK, MessageBoxAction.CANCEL]}
      >
        ¿Está seguro que desea eliminar el tipo de equipo <strong>{selectedTruckType?.name}</strong>? Esta acción no se puede deshacer.
      </MessageBox>
    </div>
  );
};

export default withPermission(TruckTypeList, 'truckstypes', 'listView');
